/**
 * Article Table of Contents Generator
 * 
 * This script generates a table of contents for blog articles by scanning
 * the article content for headings (h1, h2, h3, h4, h5, h6) and creating
 * a nested list of links to those headings.
 */

class ArticleTableOfContents {
  constructor() {
    this.tocContainers = document.querySelectorAll('.article-toc__content');
    this.articleContent = document.getElementById('article-content');
    this.tocHeaders = document.querySelectorAll('.article-toc__header');
    this.tocToggles = document.querySelectorAll('.article-toc__toggle');
    this.headings = [];
    
    console.log('TOC Containers found:', this.tocContainers.length);
    console.log('Article Content:', this.articleContent);
    console.log('TOC Toggles found:', this.tocToggles.length);
    
    if (this.tocContainers.length > 0 && this.articleContent) {
      this.init();
      this.initToggles();
    } else {
      console.log('Could not initialize table of contents: Missing container or content element');
    }
  }
  
  initToggles() {
    // Set up toggle buttons
    this.tocToggles.forEach((toggle, index) => {
      const tocContainer = this.tocContainers[index];
      const tocHeader = this.tocHeaders[index];
      
      if (!toggle) {
        console.error('Toggle button not found for container', index);
        return;
      }
      
      console.log('Setting up toggle button click handler for container', index);
      
      // Add direct click handler
      toggle.onclick = (event) => {
        event.preventDefault();
        
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
        const newState = !isExpanded;
        
        console.log('Toggle clicked, current state:', isExpanded, 'new state:', newState);
        
        // Update button state
        toggle.setAttribute('aria-expanded', newState ? 'true' : 'false');
        
        // Update content visibility
        if (tocContainer) {
          if (newState) {
            tocContainer.classList.remove('collapsed');
            tocHeader.style.marginBottom = '15px';
            tocHeader.style.paddingBottom = '10px';
            tocHeader.style.borderBottom = '1px solid rgba(0,0,0,0.1)';
          } else {
            tocContainer.classList.add('collapsed');
            tocHeader.style.margin = '0';
            tocHeader.style.padding = '0';
            tocHeader.style.border = '0';
          }
          console.log('TOC container classes:', tocContainer.className);
        }
        
        // Save state to localStorage
        localStorage.setItem('article-toc-collapsed-' + index, !newState);
        
        console.log('TOC toggled:', newState ? 'expanded' : 'collapsed');
        return false;
      };
      
      // Check if there's a saved state
      const savedState = localStorage.getItem('article-toc-collapsed-' + index);
      
      // Default to expanded (not collapsed)
      if (savedState !== 'true') {
        toggle.setAttribute('aria-expanded', 'true');
        if (tocContainer) {
          tocContainer.classList.remove('collapsed');
          tocHeader.style.marginBottom = '15px';
          tocHeader.style.paddingBottom = '10px';
          tocHeader.style.borderBottom = '1px solid rgba(0,0,0,0.1)';
        }
      } else {
        // Only collapse if explicitly set in localStorage
        toggle.setAttribute('aria-expanded', 'false');
        if (tocContainer) {
          tocContainer.classList.add('collapsed');
        }
      }
    });
  }
  
  init() {
    // Hide TOC by default before checking for headings
    this.hideTOC();
    
    // Find headings in the article (only h1-h6 elements)
    this.findHeadings();
    
    // Only show TOC if we found actual heading elements
    if (this.headings.length > 0) {
      console.log(`Found ${this.headings.length} heading elements, generating TOC...`);
      this.generateTOC();
      this.addHeadingIds();
      this.showTOC(); // Make TOC visible only if headings were found
    } else {
      console.log('No heading elements found, TOC will remain hidden...');
      // TOC is already hidden by default, but call hideTOC again to be sure
      this.hideTOC();
    }
  }
  
  findHeadings() {
    // Find all headings in the article content - ONLY standard heading elements (h1-h6)
    const headingElements = this.articleContent.querySelectorAll('h1, h2, h3, h4, h5, h6');
    console.log('Found headings:', headingElements.length);
    
    // Process standard headings only
    headingElements.forEach(heading => {
      // Skip empty headings
      if (heading.textContent.trim() === '') return;
      
      this.headings.push({
        level: parseInt(heading.tagName.substring(1)), // Get the heading level (1-6)
        text: heading.textContent.trim(),
        element: heading
      });
    });
    
    console.log('Processed headings:', this.headings.length);
  }
  
  generateTOC() {
    if (this.headings.length === 0) return;
    
    // Generate TOC for each container
    this.tocContainers.forEach(tocContainer => {
      // Clear any existing content
      if (tocContainer) {
        tocContainer.innerHTML = '';
      } else {
        console.error('TOC container is null, cannot generate table of contents');
        return;
      }
      
      const tocList = document.createElement('ul');
      let currentList = tocList;
      let prevLevel = 0;
      let listStack = [tocList];
      
      this.headings.forEach((heading, index) => {
        const listItem = document.createElement('li');
        const link = document.createElement('a');
        
        // Create a slug from the heading text for the ID
        const headingId = this.slugify(heading.text) || `heading-${index}`;
        
        link.textContent = heading.text;
        link.href = `#${headingId}`;
        
        // Add click event listener to handle smooth scrolling with offset
        link.addEventListener('click', (e) => {
          e.preventDefault();
          const targetId = link.getAttribute('href').substring(1);
          const targetElement = document.getElementById(targetId);
          
          if (targetElement) {
            // Get header height to use as offset
            const headerHeight = document.querySelector('sticky-header') ?
              document.querySelector('sticky-header').offsetHeight : 80;
            
            // Calculate the target position with offset
            const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset - headerHeight - 20;
            
            // Smooth scroll to the target position
            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });
          }
        });
        
        listItem.appendChild(link);
        
        // Handle nesting based on heading level
        if (heading.level > prevLevel) {
          // Create a new nested list
          const nestedList = document.createElement('ul');
          
          // Make sure we have a lastChild before trying to append to it
          const lastItem = listStack[listStack.length - 1].lastChild;
          if (lastItem) {
            lastItem.appendChild(nestedList);
            listStack.push(nestedList);
            currentList = nestedList;
          } else {
            // If there's no lastChild, just add to the current list
            currentList.appendChild(listItem);
          }
        } else if (heading.level < prevLevel) {
          // Go back up the stack to the appropriate level
          const levelsUp = Math.min(prevLevel - heading.level, listStack.length - 1);
          for (let i = 0; i < levelsUp; i++) {
            if (listStack.length > 1) { // Always keep at least one list in the stack
              listStack.pop();
            }
          }
          currentList = listStack[listStack.length - 1];
          currentList.appendChild(listItem);
        } else {
          // Same level, just add to current list
          currentList.appendChild(listItem);
        }
        
        prevLevel = heading.level;
      });
      
      // Only append if we have items
      if (tocList.children.length > 0) {
        tocContainer.appendChild(tocList);
      }
    });
  }
  
  addHeadingIds() {
    this.headings.forEach((heading, index) => {
      // Create a unique ID for each heading
      const headingId = this.slugify(heading.text) || `heading-${index}`;
      
      // Only set ID if element exists
      if (heading.element) {
        heading.element.id = headingId;
      } else {
        console.warn(`Cannot set ID for heading: ${heading.text} - element is null`);
      }
    });
  }
  
  slugify(text) {
    if (!text || typeof text !== 'string') {
      return 'heading';
    }
    
    const slug = text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-')     // Replace spaces with hyphens
      .replace(/--+/g, '-')     // Replace multiple hyphens with single hyphen
      .trim();                  // Trim leading/trailing whitespace
      
    // If slug is empty after processing, return a fallback
    return slug || 'heading';
  }
  
  hideTOC() {
    // Hide the TOC if there are no headings
    const tocElements = document.querySelectorAll('.article-toc');
    const tocContainers = document.querySelectorAll('.article-template__toc');
    
    // Hide both the TOC elements and their containers
    tocElements.forEach(element => {
      if (element) {
        element.style.display = 'none';
      }
    });
    
    // Also hide the container elements that wrap the TOCs
    tocContainers.forEach(container => {
      if (container) {
        container.style.display = 'none';
      }
    });
    
    console.log('TOCs hidden due to no headings found');
  }
  
  showTOC() {
    // Only show the TOC if we have headings
    if (this.headings.length === 0) {
      this.hideTOC();
      return;
    }
    
    // Ensure the TOCs are visible
    const tocElements = document.querySelectorAll('.article-toc');
    const tocContainers = document.querySelectorAll('.article-template__toc');
    
    tocElements.forEach(element => {
      if (element) {
        element.style.display = 'block';
      }
    });
    
    tocContainers.forEach(container => {
      if (container) {
        container.style.display = 'block';
      }
    });
    
    console.log('TOCs shown with', this.headings.length, 'headings');
  }
}

// Hide TOC by default before any initialization attempts
function hideTableOfContents() {
  const tocElements = document.querySelectorAll('.article-toc');
  const tocContainers = document.querySelectorAll('.article-template__toc');
  
  tocElements.forEach(element => {
    if (element) {
      element.style.display = 'none';
    }
  });
  
  tocContainers.forEach(container => {
    if (container) {
      container.style.display = 'none';
    }
  });
  
  console.log('TOCs hidden by default');
}

// Initialize the table of contents when the DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  console.log('DOM loaded, hiding TOC by default...');
  
  // Hide TOC by default before any initialization attempts
  hideTableOfContents();
  
  // Add a longer delay to ensure content is fully loaded
  setTimeout(() => {
    console.log('First TOC initialization attempt...');
    try {
      new ArticleTableOfContents();
    } catch (error) {
      console.error('Error initializing TOC on first attempt:', error);
      hideTableOfContents(); // Ensure TOC is hidden if there's an error
    }
    
    // If no headings were found on first try, make another attempt
    setTimeout(() => {
      const tocContainers = document.querySelectorAll('.article-toc__content');
      let allEmpty = true;
      
      tocContainers.forEach(container => {
        if (container && container.children.length > 0) {
          allEmpty = false;
        }
      });
      
      if (allEmpty && tocContainers.length > 0) {
        console.log('No headings found on first attempt, trying again...');
        try {
          new ArticleTableOfContents();
        } catch (error) {
          console.error('Error initializing TOC on second attempt:', error);
          hideTableOfContents(); // Ensure TOC is hidden if there's an error
        }
        
        // Make a third attempt with an even longer delay
        setTimeout(() => {
          const tocContainers = document.querySelectorAll('.article-toc__content');
          let allEmpty = true;
          
          tocContainers.forEach(container => {
            if (container && container.children.length > 0) {
              allEmpty = false;
            }
          });
          
          if (allEmpty && tocContainers.length > 0) {
            console.log('No headings found on second attempt, trying one last time...');
            try {
              new ArticleTableOfContents();
            } catch (error) {
              console.error('Error initializing TOC on third attempt:', error);
              // Hide the TOC if all attempts failed
              hideTableOfContents();
            }
          }
        }, 2000);
      }
    }, 1500);
  }, 1000);
});

// Also initialize when window is fully loaded (including images)
window.addEventListener('load', () => {
  console.log('Window fully loaded, checking if TOCs need initialization...');
  const tocContainers = document.querySelectorAll('.article-toc__content');
  
  if (tocContainers.length === 0) {
    // If no TOC containers exist, hide the TOC
    hideTableOfContents();
    return;
  }
  
  // Check if all TOC containers are empty
  let allEmpty = true;
  tocContainers.forEach(container => {
    if (container && container.children.length > 0) {
      allEmpty = false;
    }
  });
  
  // If all TOCs are empty, try to initialize again or hide them
  if (allEmpty) {
    console.log('TOCs still empty after window load, initializing again...');
    try {
      new ArticleTableOfContents();
    } catch (error) {
      console.error('Error initializing TOCs on window load:', error);
      // Hide the TOCs if the attempt failed
      hideTableOfContents();
    }
  }
  
  // Final check - if TOCs are empty after all attempts, hide them
  setTimeout(() => {
    const tocContainers = document.querySelectorAll('.article-toc__content');
    let allEmpty = true;
    
    tocContainers.forEach(container => {
      if (container && container.children.length > 0) {
        allEmpty = false;
      }
    });
    
    if (allEmpty) {
      console.log('Final check: All TOCs are empty, hiding them');
      hideTableOfContents();
    }
  }, 500);
});